// ============================================================================
// 프로젝트 알림 설정 탭 (CHG-048: REQ-V48-014)
// ============================================================================

import { useState, useEffect } from 'react';
import { Loader2, CheckCircle, AlertCircle, Bell, Mail, MessageSquare } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../lib/axios';
import type { ApiResponse } from '@maios/shared';

interface Props {
  projectId: string;
  isProjectManager: boolean;
}

interface NotifSetting {
  slackWebhookUrl: string | null;
  emailEnabled: boolean;
  slackEnabled: boolean;
  notifyOnSlaViolation: boolean;
  notifyOnCiFailure: boolean;
  notifyOnReviewReminder: boolean;
  reminderIntervalHours: number;
}

const DEFAULTS: NotifSetting = {
  slackWebhookUrl: null,
  emailEnabled: false,
  slackEnabled: false,
  notifyOnSlaViolation: true,
  notifyOnCiFailure: true,
  notifyOnReviewReminder: true,
  reminderIntervalHours: 8,
};

export default function NotificationSettingsTab({ projectId, isProjectManager }: Props) {
  const [settings, setSettings] = useState<NotifSetting>(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [slackTestStatus, setSlackTestStatus] = useState<'idle' | 'testing' | 'ok' | 'error'>('idle');
  const [slackUrl, setSlackUrl] = useState('');

  useEffect(() => {
    api.get<ApiResponse<NotifSetting>>(`/projects/${projectId}/notification-settings`)
      .then((res) => {
        const d = res.data.data!;
        setSettings(d);
        setSlackUrl(d.slackWebhookUrl ?? '');
      })
      .catch((err) => {
        console.warn('[NotificationSettings] load failed:', err);
        toast.error('알림 설정을 불러오지 못했습니다.');
      })
      .finally(() => setLoading(false));
  }, [projectId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/projects/${projectId}/notification-settings`, {
        ...settings,
        slackWebhookUrl: slackUrl.trim() || null,
      });
      toast.success('알림 설정이 저장되었습니다.');
    } catch {
      toast.error('저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleSlackTest = async () => {
    setSlackTestStatus('testing');
    try {
      // Save URL first
      await api.put(`/projects/${projectId}/notification-settings`, {
        slackWebhookUrl: slackUrl.trim() || null,
      });
      const res = await api.post<ApiResponse<{ status: string; message?: string }>>(`/projects/${projectId}/notification-settings/test-slack`);
      if (res.data.data?.status === 'ok') {
        setSlackTestStatus('ok');
        toast.success('Slack 테스트 메시지가 전송되었습니다.');
      } else {
        setSlackTestStatus('error');
        toast.error(res.data.data?.message ?? 'Slack 전송 실패');
      }
    } catch {
      setSlackTestStatus('error');
      toast.error('Slack 테스트에 실패했습니다.');
    }
  };

  const toggle = (key: keyof NotifSetting) => {
    setSettings((s) => ({ ...s, [key]: !s[key] }));
  };

  if (loading) {
    return <div className="flex items-center justify-center py-8"><Loader2 className="size-6 animate-spin text-gray-400" /></div>;
  }

  return (
    <div className="space-y-6">
      {/* 채널 설정 */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Bell className="size-4" /> 알림 채널
        </h3>

        {/* Email */}
        <div className="flex items-center justify-between py-3 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Mail className="size-4 text-gray-500" />
            <div>
              <div className="text-sm font-medium text-gray-700">이메일 알림</div>
              <div className="text-sm text-gray-400">프로젝트 멤버 이메일로 알림을 발송합니다.</div>
            </div>
          </div>
          <Toggle checked={settings.emailEnabled} onChange={() => toggle('emailEnabled')} disabled={!isProjectManager} />
        </div>

        {/* Slack */}
        <div className="py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MessageSquare className="size-4 text-gray-500" />
              <div>
                <div className="text-sm font-medium text-gray-700">Slack 알림</div>
                <div className="text-sm text-gray-400">Slack 채널로 알림을 발송합니다.</div>
              </div>
            </div>
            <Toggle checked={settings.slackEnabled} onChange={() => toggle('slackEnabled')} disabled={!isProjectManager} />
          </div>
          {settings.slackEnabled && (
            <div className="mt-3 ml-6 space-y-2">
              <input
                type="password"
                value={slackUrl}
                onChange={(e) => { setSlackUrl(e.target.value); setSlackTestStatus('idle'); }}
                placeholder="https://hooks.slack.com/services/..."
                disabled={!isProjectManager}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-50"
              />
              <div className="flex items-center gap-2">
                <button
                  onClick={handleSlackTest}
                  disabled={!slackUrl.trim() || slackTestStatus === 'testing' || !isProjectManager}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 disabled:opacity-50"
                >
                  {slackTestStatus === 'testing' && <Loader2 className="size-3 animate-spin" />}
                  테스트 전송
                </button>
                {slackTestStatus === 'ok' && <span className="text-sm text-green-600 flex items-center gap-1"><CheckCircle className="size-3" /> 성공</span>}
                {slackTestStatus === 'error' && <span className="text-sm text-red-600 flex items-center gap-1"><AlertCircle className="size-3" /> 실패</span>}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 알림 유형 */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-gray-900 mb-4">알림 유형</h3>

        <div className="space-y-3">
          <ToggleRow
            label="SLA 위반 알림"
            description="코드 리뷰 SLA를 초과한 PR에 대해 알림을 발송합니다."
            checked={settings.notifyOnSlaViolation}
            onChange={() => toggle('notifyOnSlaViolation')}
            disabled={!isProjectManager}
          />
          <ToggleRow
            label="CI 실패 알림"
            description="CI/CD 파이프라인이 실패하면 PR 작성자에게 알림을 발송합니다."
            checked={settings.notifyOnCiFailure}
            onChange={() => toggle('notifyOnCiFailure')}
            disabled={!isProjectManager}
          />
          <ToggleRow
            label="리뷰 리마인더"
            description="리뷰 요청 후 일정 시간이 지나면 리뷰어에게 리마인더를 발송합니다."
            checked={settings.notifyOnReviewReminder}
            onChange={() => toggle('notifyOnReviewReminder')}
            disabled={!isProjectManager}
          />

          {settings.notifyOnReviewReminder && (
            <div className="ml-6 flex items-center gap-2">
              <span className="text-sm text-gray-600">리마인더 주기:</span>
              <input
                type="number"
                min={1}
                max={168}
                value={settings.reminderIntervalHours}
                onChange={(e) => setSettings((s) => ({ ...s, reminderIntervalHours: parseInt(e.target.value, 10) || 8 }))}
                disabled={!isProjectManager}
                className="w-16 px-2 py-1 border border-gray-300 rounded text-sm text-center outline-none focus:ring-1 focus:ring-primary-500 disabled:bg-gray-50"
              />
              <span className="text-sm text-gray-500">시간</span>
            </div>
          )}
        </div>
      </div>

      {/* Save */}
      {isProjectManager && (
        <button
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
        >
          {saving && <Loader2 className="size-4 animate-spin" />}
          저장
        </button>
      )}
    </div>
  );
}

// --- Sub-components ---

function Toggle({ checked, onChange, disabled }: { checked: boolean; onChange: () => void; disabled?: boolean }) {
  return (
    <button
      onClick={onChange}
      disabled={disabled}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${checked ? 'bg-primary-600' : 'bg-gray-200'} ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${checked ? 'translate-x-4' : 'translate-x-0.5'}`} />
    </button>
  );
}

function ToggleRow({ label, description, checked, onChange, disabled }: {
  label: string; description: string; checked: boolean; onChange: () => void; disabled?: boolean;
}) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-gray-50">
      <div>
        <div className="text-sm font-medium text-gray-700">{label}</div>
        <div className="text-sm text-gray-400">{description}</div>
      </div>
      <Toggle checked={checked} onChange={onChange} disabled={disabled} />
    </div>
  );
}
